import { getFormProps, getInputProps, getSelectProps, getTextareaProps, useForm } from "@conform-to/react";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { invariantResponse } from "@epic-web/invariant";
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption } from "@headlessui/react";
import { ArrowTopRightOnSquareIcon, CheckIcon } from "@heroicons/react/24/outline";
import { type ActionFunctionArgs, type LoaderFunctionArgs } from "@remix-run/node";
import { Form, Link, type MetaFunction, json, redirect, useActionData, useLoaderData, useSearchParams } from "@remix-run/react"
import clsx from "clsx";
import { useState } from "react";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { z } from "zod";
import { brandName, brandUrl } from "#app/components/branding.js"
import { GeneralErrorBoundary } from "#app/components/error-boundary.js";
import { CheckboxField, ErrorList, Field, FieldSelect, TextareaField } from '#app/components/forms.tsx'
import { TagsInputField } from "#app/components/ui/input-tags.js";
import { Label } from "#app/components/ui/label.js";
import { StatusButton } from "#app/components/ui/status-button.js";
import { getUserId } from "#app/utils/auth.server.js";
import { prisma } from "#app/utils/db.server.js";
import { checkHoneypot } from "#app/utils/honeypot.server.js";
import { useIsPending } from "#app/utils/misc.js";
import { NewScriptSchema, sanitizeSlug } from "#app/utils/scripts.helper.js";
import { editScript, getScriptBySlug } from "#app/utils/scripts.server.js";
import { getServerList, getFileTypes } from "#app/utils/selected-server-options.js";
import { useOptionalUser } from "#app/utils/user.js";



export async function loader({ request, params }: LoaderFunctionArgs) {
	const result = await getScriptBySlug(params.scriptSlug || "")
	invariantResponse(result, 'Script not found', { status: 404 })
	const loggedInUser = await getUserId(request);
	if (loggedInUser == null || loggedInUser !== result.creatorId) {
		invariantResponse(null, 'You do not have permission to edit this script', { status: 403 })
	}
	return json({serverList: getServerList(),
		result })
}

export async function action({ request, params }: ActionFunctionArgs) {

	const result = await getScriptBySlug(params.scriptSlug || "")
	invariantResponse(result, 'Script not found', { status: 404 })

	const formData = await request.formData()

	checkHoneypot(formData)
	const userId = await getUserId(request)

	const submission = await parseWithZod(formData, {
		schema: NewScriptSchema.superRefine(async (data, ctx) => {
			const existingScript = await prisma.script.findUnique({
				where: { slug: sanitizeSlug(data.name, userId) },
				select: { id: true },
			})

			if (existingScript && existingScript.id !== result.id) {
				ctx.addIssue({
					path: ['slug'],
					code: z.ZodIssueCode.custom,
					message: 'A script with this slug already exists',
				})
				return
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}


    const response = await editScript(result.slug, submission.value, userId)

	if (response.status === 'success') {
		return redirect(response.redirect)
	} else {
		return json(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}



export default function EditScript() {
	const user = useOptionalUser()
    const data = useLoaderData<typeof loader>()
    const servers = data.serverList;
  
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()

    const [selectedServer, setSelectedServer] = useState<string | null>(data.result.server)
    const [queryServer, setQueryServer] = useState('')
  
    const [fileType, setFileType] = useState<string | null>(data.result.fileType)
  
    const filteredServers =
     queryServer === ''
        ? servers
        : servers.filter((server) => {
            return server.toLowerCase().includes(queryServer.toLowerCase())
          })

	const [form, fields] = useForm({
		id: 'new-script-form',
		constraint: getZodConstraint(NewScriptSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			const result = parseWithZod(formData, { schema: NewScriptSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
		defaultValue: {
			name: data.result.title,
			contents: data.result.content,
			description: data.result.description,
			server: data.result.server,
			category: data.result.category,
			skills: data.result.skills,
			tags: data.result.tags,
			showcase: data.result.showcase,
			associated: data.result.associated,
			hotkey: data.result.hotkey,
			hotkeyAlt: data.result.hotkeyAlt,
			hotkeyShift: data.result.hotkeyShift,
			hotkeyCtrl: data.result.hotkeyCtrl,
            fileType: data.result.fileType,
		}
	})

    const serverFormId = `${form.id}-server`
    const errorServerId = fields.server.errors?.length ? `${serverFormId}-error` : undefined


	return 		<div className="container mb-48 mt-6 flex flex-col items-center ">
	<div className="flex flex-wrap items-center justify-center">
			<h1 className="text-center text-h2">Edit Script</h1>
	</div>
	<p className="mt-2 text-center text-muted-foreground">
			Back to: {' '}
			<Link to={`/script/${data.result.slug}`} className="hover:no-underline"><span className="underline">{data.result.slug}</span>
				<ArrowTopRightOnSquareIcon aria-hidden="true" className="h-6 w-6 inline p-1" />
			</Link>
		</p>
  <div className="container flex flex-col rounded-3xl bg-muted mt-4 pt-4 p-12">
    <Form method="POST" {...getFormProps(form)}>
        <HoneypotInputs />

        <div className="flex flex-col md:flex-row">


            <Field
                className="md:w-1/2 md:mr-4"
                labelProps={{
                    htmlFor: fields.name.id,
                    children: 'Script Name (required)',
                }}
                
                inputProps={{
                    ...getInputProps(fields.name, { type: 'text' }),
                    autoFocus: true,
                    placeholder: 'Enter your script name',
                    autoComplete: 'off'
                }}
                errors={fields.name.errors}
            />
            <Field
                className="md:w-1/3 md:mr-4"
                labelProps={{
                    htmlFor: fields.slug.id,
                    children: 'Script Link Preview',
                }}
                inputProps={{
                    ...getInputProps(fields.slug, { type: 'text' }),
                    readOnly: true,
                    value: `${brandUrl.toLocaleLowerCase()}/script/${sanitizeSlug(fields.name.value, user?.id)}`,
                    autoFocus: true,
                    disabled: true,
                    autoComplete: 'off',
                }}
                errors={fields.slug.errors}
            />
            <div className="md:w-1/3 mr-4">
                <Label htmlFor={fields.server.id}>Server (required)
                    <Combobox immediate name={"server"} value={selectedServer} onChange={setSelectedServer} onClose={() => setQueryServer('')}>
                        <ComboboxInput id={fields.server.id} 
                        aria-label="server"
                        placeholder="Select your server"
                        autoComplete="off"
                        autoFocus={false}
                        className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid'
                        displayValue={(server: string) => server}
                        onChange={(event) => setQueryServer(event.target.value)}
                        />
                        <ComboboxOptions anchor="bottom start" 
                            transition
                            className="origin-top border transition duration-200 ease-out empty:invisible data-[closed]:scale-95 data-[closed]:opacity-0"
                            //className="border empty:invisible"
                        >
                        {filteredServers.map((server) => (
                            <ComboboxOption key={server} value={server} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid data-[focus]:bg-blue-500">
                            {({ focus, selected }) => (
                            <div className={clsx('group flex gap-2', focus)}>
                                {selected && <CheckIcon className="size-5" />}
                                {server}
                            </div>
                            )}
                            </ComboboxOption>
                        ))}
                        </ComboboxOptions>
                    </Combobox>
                    <div className="min-h-[32px] px-4 pb-3 pt-1">
                        {errorServerId ? <ErrorList id={errorServerId} errors={fields.server.errors} /> : null}
                    </div>
                </Label>
            </div>
        </div>
        <div className={`flex flex-col md:flex-row`}>
        <TextareaField
                    className="md:w-1/2 md:mr-4"
                    labelProps={{ children: 'Script Contents (required)' }}
                    textareaProps={{
                        ...getTextareaProps(fields.contents),
                        placeholder: 'Enter your script here',
                    }}
                    errors={fields.contents.errors}
                />

        <TextareaField
                    className="md:w-1/2"
                    labelProps={{ children: 'Description (optional)' }}
                    textareaProps={{
                        ...getTextareaProps(fields.description),
                    }}
                    errors={fields.description.errors}
                />
            

        </div>

        <div className={`flex flex-col md:flex-row`}>
            <Field
                className="md:w-1/3"
                labelProps={{
                    htmlFor: fields.category.id,
                    children: 'Category (optional)',
                }}
                inputProps={{
                    ...getInputProps(fields.category, { type: 'text' }),
                    autoFocus: false,
                    //placeholder: 'Training, PvP, etc.',
                    autoComplete: 'off',
                }}
                errors={fields.category.errors}
            />
            <Field
                className="md:w-1/3"
                labelProps={{
                    htmlFor: fields.skills.id,
                    children: 'Related Skills (optional)',
                }}
                inputProps={{
                    ...getInputProps(fields.skills, { type: 'text' }),
                    autoFocus: false,
                    placeholder: 'Alchemy, Blacksmithing, etc.',
                }}
                errors={fields.skills.errors}
            />
            <TagsInputField 
                className="md:w-1/3"
                labelProps={{
                    htmlFor: fields.tags.id,
                    children: 'Tags (optional)',
                }}
                inputProps={{
                    ...getInputProps(fields.tags, { type: 'text' }),
                    autoFocus: false,
                    autoComplete: 'off',
                }}
                errors={fields.tags.errors}
            />

        </div>
        <div className={`flex flex-col md:flex-row`}>
            <Field
                className="md:w-1/3"
                labelProps={{
                    htmlFor: fields.showcase.id,
                    children: 'Showcase Clip (YouTube / Twitch) (optional)',
                }}
                inputProps={{
                    ...getInputProps(fields.showcase, { type: 'text' }),
                    autoFocus: false,
                    autoComplete: 'off',
                }}
                errors={fields.showcase.errors}
            />
            <Field
                className="md:w-1/4"
                labelProps={{
                    htmlFor: fields.associated.id,
                    children: 'Associated Scripts (optional)',
                }}
                inputProps={{
                    ...getInputProps(fields.associated, { type: 'text' }),
                    autoFocus: false,
                    autoComplete: 'off',
                }}
                errors={fields.associated.errors}
            />

            <FieldSelect
                className="md:w-1/4"
                labelProps={{
                    htmlFor: fields.fileType.id,
                    children: 'File type (optional)',
                }}
                options={getFileTypes()}
                onChange={setFileType}
                selectProps={{
                    ...getSelectProps(fields.fileType),
                    value: fileType ?? '',
                    key: fields.fileType.id,
                    form: form.id,
                    className: '',
                    placeholder: fileType ? fileType : 'Select a file type',
                }}
                errors={fields.fileType.errors}
            />
            <Field
                className="w-16"
                labelProps={{
                    htmlFor: fields.hotkey.id,
                    children: 'Hotkey',
                }}
                inputProps={{
                    ...getInputProps(fields.hotkey, { type: 'text' }),
                    autoFocus: false,
                    autoComplete: 'off',
                    className: "w-12"
                }}
                errors={fields.hotkey.errors}
            />
            <div className="flex flex-col md:w-1/8">

            <CheckboxField
                className=""
                labelProps={{
                    htmlFor: fields.hotkeyCtrl.id,
                    children: 'CTRL',
                }}
                buttonProps={getInputProps(fields.hotkeyCtrl, {
                    type: 'checkbox',
                })}
                errors={fields.hotkeyCtrl.errors}
            />

            <CheckboxField
				className="-mt-2"
                labelProps={{
                    htmlFor: fields.hotkeyAlt.id,
                    children: 'ALT',
                }}
                buttonProps={getInputProps(fields.hotkeyAlt, {
                    type: 'checkbox',
                })}
                errors={fields.hotkeyAlt.errors}
            />

            <CheckboxField
                className="-mt-2"
                labelProps={{
                    htmlFor: fields.hotkeyShift.id,
                    children: 'SHIFT',
                }}
                buttonProps={getInputProps(fields.hotkeyShift, {
                    type: 'checkbox',
                })}
                errors={fields.hotkeyShift.errors}
            />



        </div>
            </div>

        <ErrorList errors={form.errors} id={form.errorId} />
        
        <StatusButton
            className="w-full"
            status={isPending ? 'pending' : (form.status ?? 'idle')}
            type="submit"
            disabled={isPending}
        >
            Update the script {user ? `as ${user.username}` : 'anonymously'}
        </StatusButton>

    </Form>
	</div>
</div>

}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.result.title ?? params.scriptSlug

	return [
		{ title: `Edit | ${displayName} | ${brandName}` },
		{
			name: 'description',
			content: `Script ${displayName} on ${brandName}`,
		},
		{
			tagName: "link",
			rel: "canonical",
			href: `https://uoaddicts.com/script/${data?.result.slug ?? params.scriptSlug}`
		}
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				403: ({ params }) => (
					<div className="text-center">
						<p className="m-4">You do not have permission to edit: <br />"{params.scriptSlug}"<br /> make sure you are logged in to confirm ownership.</p>
						<Link to={`/script/${params.scriptSlug}`} className="text-xl underline hover:no-underline">&lt; Back to viewing script</Link>
					</div>					
				),
				404: ({ params }) => (
					<div className="text-center">
						<p className="m-4">Cannot find a script related to: <br />"{params.scriptSlug}"<br /> reach out if you think it should be added.</p>
						<Link to="/scripts" className="text-xl underline hover:no-underline">&lt; Back to all scripts</Link>
						<Link to="/script/new" className="ml-8 text-xl underline hover:no-underline">Add it myself</Link>
					</div>					
				),
			}}
		/>
	)
}



